import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SignatureCanvas from 'react-signature-canvas';
import { 
  Calendar, Clock, MapPin, User, Phone, 
  FileText, AlertTriangle, Check, ArrowLeft, ArrowRight, 
  Zap, Car, Camera, Home, Tag, Gift, X, Plus, Minus,
  ShoppingBag, CreditCard, DollarSign, Receipt, Trash2, Truck, Package, AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, bookingsAPI, waiversAPI, mileageAPI, couponsAPI, referralsAPI, addonsAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import CleaningDetailSheet from '../components/CleaningDetailSheet';
import ServiceDetailForms from '../components/booking/ServiceDetailForms';
import ServiceAgreementForm from '../components/ServiceAgreementForm';
import { NonMedicalDisclaimer } from '../components/NonMedicalDisclaimer';
import TimeEstimateDisplay from '../components/booking/TimeEstimateDisplay';
import CourierBookingForm from '../components/booking/CourierBookingForm';

const BookingPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const signatureRef = useRef();
  
  // Guest checkout state
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [rememberGuestInfo, setRememberGuestInfo] = useState(true);
  
  // Load saved guest info from localStorage
  const getSavedGuestInfo = () => {
    try {
      const saved = localStorage.getItem('nc_guest_info');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return { full_name: '', email: '', phone: '' };
  };
  
  const [guestInfo, setGuestInfo] = useState(getSavedGuestInfo);
  
  const [service, setService] = useState(null);
  const [waivers, setWaivers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [showWaiver, setShowWaiver] = useState(false);
  
  // First-time customer discount
  const [isFirstTimeCustomer, setIsFirstTimeCustomer] = useState(false);
  const [firstTimeDiscount, setFirstTimeDiscount] = useState(0);
  
  // Coupon code
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  
  // Referral credits
  const [referralCredits, setReferralCredits] = useState({ balance: 0, has_credits: false });
  const [creditsToApply, setCreditsToApply] = useState(0);
  const [useCredits, setUseCredits] = useState(false);
  
  // Add-ons for cleaning services
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({}); // { addonId: quantity }
  const [isWithin3Days, setIsWithin3Days] = useState(false); // Track if booking date is within 3 days
  
  // Courier-specific booking mode
  const [useCourierForm, setUseCourierForm] = useState(false);
  
  const [formData, setFormData] = useState({
    service_id: serviceId,
    scheduled_date: '',
    scheduled_time: '',
    duration_hours: 2,
    address: '',
    city: '',
    state: '',
    zip_code: '',
    special_instructions: '',
    is_recurring: false,
    recurring_frequency: '',
    recurring_end_date: '',
    waiver_signed: false,
    waiver_signature: '',
    non_solicitation_agreed: false,
    non_medical_acknowledged: false, // NEW: Customer acknowledges non-medical services only
    // Payment method
    payment_method: 'stripe', // 'stripe' or 'cash'
    cash_payment_waiver_agreed: false, // Required for cash payments
    client_name: user?.full_name || '',
    client_phone: user?.phone || '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    urgency_level: 'standard',
    distance_miles: 0,
    mileage_fee: 0,
    // Cleaning/Service Info with Photos/Videos
    cleaning_info: {
      property_type: 'house',
      num_bedrooms: 2,
      num_bathrooms: 1,
      square_footage: '',
      num_floors: 1,
      has_pets: false,
      pet_types: [],
      areas_to_clean: [],
      special_areas: [],
      priority_areas: [],
      photos: [],
      videos: [],
      deep_cleaning_needed: false,
      move_in_move_out: false,
      post_construction: false,
      regular_maintenance: false,
      customer_provides_supplies: false,
      access_instructions: '',
      alarm_code: '',
      key_location: '',
      eco_friendly_products: false,
      specific_products_required: '',
      areas_to_avoid: [],
      special_instructions: ''
    },
    // Service Agreement - Customer only gets what is paid for
    service_agreement: {
      cleaning_liability_waiver: false,
      cleaning_damage_waiver: false,
      cleaning_access_consent: false,
      service_scope_acknowledged: false,
      payment_terms_agreed: false,
      cancellation_policy_agreed: false,
      non_solicitation_agreed: false,
      customer_signature: '',
      signature_date: ''
    },
    // Pickup/Errand Service Options
    payment_type: 'standard',  // standard, pickup_prepay, pickup_paid
    pickup_budget: '',  // Estimated budget for pickup services (prepay only)
    pickup_items: [],  // Shopping list items
    
    // Service-specific info objects
    laundry_info: {
      estimated_loads: '',
      service_level: '',
      laundry_types: [],
      pickup_required: false,
      delivery_required: false,
      detergent_preference: '',
      special_instructions: ''
    },
    courier_info: {
      pickup_address: '',
      pickup_contact_name: '',
      pickup_contact_phone: '',
      package_description: '',
      package_size: '',
      estimated_weight: '',
      is_fragile: false,
      special_instructions: ''
    },
    errands_info: {
      errand_types: [],
      task_list: '',
      purchase_budget: 0,
      num_stops: 1,
      receipts_required: false,
      special_instructions: ''
    },
    tasks_info: {
      task_categories: [],
      task_description: '',
      tools_provided: '',
      difficulty_level: '',
      supplies_budget: 0,
      special_instructions: ''
    }
  });

  const [mileageInfo, setMileageInfo] = useState(null);
  const [calculatingMileage, setCalculatingMileage] = useState(false);
  
  // Time Estimate State
  const [timeEstimate, setTimeEstimate] = useState(null);
  const [estimatingTime, setEstimatingTime] = useState(false);
  const [timeEstimateError, setTimeEstimateError] = useState(null);

  // Save guest info to localStorage when it changes (if remember is enabled)
  useEffect(() => {
    if (isGuestCheckout && guestInfo.email) {
      if (rememberGuestInfo) {
        try {
          localStorage.setItem('nc_guest_info', JSON.stringify(guestInfo));
        } catch (e) {
          // Ignore localStorage errors
        }
      } else {
        // Clear stored info if user unchecks "remember me"
        try {
          localStorage.removeItem('nc_guest_info');
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    }
  }, [guestInfo, isGuestCheckout, rememberGuestInfo]);

  useEffect(() => {
    fetchData();
    // Only fetch authenticated-user features if logged in
    if (isAuthenticated) {
      checkFirstTimeStatus();
      fetchReferralCredits();
    }
  }, [serviceId, isAuthenticated]);

  const fetchData = async () => {
    try {
      const [serviceRes, waiversRes] = await Promise.all([
        servicesAPI.getOne(serviceId),
        waiversAPI.getAll()
      ]);
      setService(serviceRes.data);
      setWaivers(waiversRes.data);
      setFormData(prev => ({ ...prev, duration_hours: serviceRes.data.duration_hours }));
      
      // Enable dedicated courier form for courier services
      if (serviceRes.data.service_type === 'courier') {
        setUseCourierForm(true);
      }
      
      // Fetch add-ons if this is a cleaning service
      if (serviceRes.data.service_type) {
        fetchAddons(serviceRes.data.service_type);
      }
    } catch (error) {
      toast.error('Failed to load service details');
      // Redirect guests to services page, logged in users to dashboard
      navigate(isAuthenticated ? '/customer/dashboard' : '/services');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAddons = async (serviceType) => {
    try {
      const response = await addonsAPI.getForService(serviceType);
      setAvailableAddons(response.data || []);
    } catch (error) {
      console.error('Failed to fetch add-ons:', error);
      setAvailableAddons([]);
    }
  };
  
  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev => {
      if (prev[addonId]) {
        const { [addonId]: removed, ...rest } = prev;
        return rest;
      } else {
        return { ...prev, [addonId]: 1 };
      }
    });
  };
  
  const handleAddonQuantity = (addonId, delta) => {
    setSelectedAddons(prev => {
      const current = prev[addonId] || 0;
      const newQty = Math.max(0, Math.min(10, current + delta));
      if (newQty === 0) {
        const { [addonId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [addonId]: newQty };
    });
  };
  
  const calculateAddonsTotal = () => {
    return Object.entries(selectedAddons).reduce((total, [addonId, qty]) => {
      const addon = availableAddons.find(a => a.id === addonId);
      if (addon) {
        // Use the addon's price directly (already set per the price_type)
        return total + (addon.price * qty);
      }
      return total;
    }, 0);
  };
  
  const getSelectedAddonsDetails = () => {
    return Object.entries(selectedAddons).map(([addonId, qty]) => {
      const addon = availableAddons.find(a => a.id === addonId);
      if (addon) {
        return {
          add_on_id: addonId,
          name: addon.name,
          price: addon.price,
          quantity: qty,
          total: addon.price * qty
        };
      }
      return null;
    }).filter(Boolean);
  };

  const formatAddonPrice = (addon) => {
    const price = `$${addon.price.toFixed(2)}`;
    if (addon.price_type === 'percentage') return `${addon.price}%`;
    if (addon.price_type === 'per_hour') return `${price}/hr`;
    if (addon.price_type === 'per_window') return `${price}/window`;
    if (addon.price_type === 'per_stop') return `${price}/stop`;
    return price;
  };

  const checkFirstTimeStatus = async () => {
    // Skip for guest checkout - requires authentication
    if (!isAuthenticated) return;
    
    try {
      const response = await couponsAPI.checkFirstTime();
      if (response.data.is_first_time) {
        setIsFirstTimeCustomer(true);
        setFirstTimeDiscount(response.data.discount_percentage);
        toast.success('Welcome! 10% first-time customer discount applied!', {
          icon: '🎁',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Failed to check first-time status:', error);
    }
  };

  const fetchReferralCredits = async () => {
    // Skip for guest checkout - requires authentication
    if (!isAuthenticated) return;
    
    try {
      const response = await referralsAPI.getCreditBalance();
      if (response.data.has_credits) {
        setReferralCredits(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch referral credits:', error);
    }
  };

  const handleToggleCredits = (checked) => {
    setUseCredits(checked);
    if (checked && referralCredits.total_available > 0) {
      // Apply up to the maximum available or total cost, whichever is smaller
      const maxApplicable = Math.min(referralCredits.total_available, parseFloat(calculateFees().total));
      setCreditsToApply(maxApplicable);
    } else {
      setCreditsToApply(0);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    try {
      const fees = calculateFees();
      const response = await couponsAPI.validate(
        couponCode.trim(), 
        parseFloat(fees.total),
        service?.service_type
      );
      
      if (response.data.valid) {
        setCouponApplied(response.data);
        toast.success(response.data.message, { icon: '🎉' });
      } else {
        toast.error(response.data.message);
        setCouponApplied(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    toast.info('Coupon removed');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Check if date is within 3 days and auto-set urgent
    if (name === 'scheduled_date' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffTime = selectedDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 3) {
        setIsWithin3Days(true);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          urgency_level: 'urgent' // Auto-set to urgent
        }));
        toast.info('Bookings within 3 days require urgent scheduling (+25% fee)', {
          icon: '⚡',
          duration: 5000
        });
        return;
      } else {
        setIsWithin3Days(false);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-calculate mileage when zip code is updated
    if (name === 'zip_code' && value.length === 5) {
      calculateMileage(value);
    }
  };

  const calculateMileage = async (zipCode) => {
    if (!zipCode || zipCode.length !== 5) return;
    
    setCalculatingMileage(true);
    try {
      const response = await mileageAPI.calculate(zipCode);
      const data = response.data;
      
      setMileageInfo(data);
      setFormData(prev => ({
        ...prev,
        distance_miles: data.distance_miles,
        mileage_fee: data.mileage_fee
      }));
      
      if (data.distance_miles > 10) {
        toast.info(`Distance: ${data.distance_miles} miles. Mileage fee: $${data.mileage_fee.toFixed(2)}`);
      } else {
        toast.success(`Distance: ${data.distance_miles} miles. No mileage fee (within 10 miles)`);
      }
      
      // Trigger time estimate recalculation with new distance
      if (service) {
        calculateTimeEstimate(data.distance_miles);
      }
    } catch (error) {
      console.error('Mileage calculation failed:', error);
      // Don't show error toast, just continue without mileage
    } finally {
      setCalculatingMileage(false);
    }
  };

  // Calculate time estimate based on job details
  const calculateTimeEstimate = async (distanceMiles = null) => {
    if (!service) return;
    
    setEstimatingTime(true);
    setTimeEstimateError(null);
    
    try {
      // Build request data
      const estimateData = {
        service_type: service.service_type || service.category || 'cleaning',
        service_id: service.id,
        cleaning_info: formData.cleaning_info,
        courier_info: formData.courier_info,
        selected_addons: Object.entries(selectedAddons)
          .filter(([_, qty]) => qty > 0)
          .map(([id, qty]) => {
            const addon = availableAddons.find(a => a.id === id);
            return { id, name: addon?.name, quantity: qty };
          }),
        distance_miles: distanceMiles ?? formData.distance_miles ?? 0,
        difficulty_level: formData.tasks_info?.difficulty_level || formData.cleaning_info?.deep_cleaning_needed ? 'heavy' : 'moderate',
        special_instructions: formData.special_instructions || formData.cleaning_info?.special_instructions || '',
        urgency_level: formData.urgency_level || 'standard',
        num_tasks: formData.errands_info?.num_stops || formData.tasks_info?.task_categories?.length || null,
        num_stops: formData.courier_info ? 2 : (formData.errands_info?.num_stops || null),
        num_packages: formData.courier_info?.package_size ? 1 : null,
        pickup_items: formData.pickup_items || []
      };
      
      const response = await bookingsAPI.estimateTime(estimateData);
      
      if (response.data.success) {
        setTimeEstimate(response.data.estimate);
      } else {
        setTimeEstimate(response.data.estimate); // Use default estimate
      }
    } catch (error) {
      console.error('Time estimation failed:', error);
      setTimeEstimateError('Unable to calculate time estimate');
      // Set a default estimate
      setTimeEstimate({
        estimated_labor_minutes: 60,
        estimated_travel_minutes: 30,
        estimated_total_minutes: 90,
        labor_display: '1 hour',
        travel_display: '30 minutes',
        total_display: '1 hour 30 min',
        confidence: 'low'
      });
    } finally {
      setEstimatingTime(false);
    }
  };
  
  // Trigger time estimate when step changes to 3 or 4
  useEffect(() => {
    if (service && (step === 3 || step === 4 || step === 5)) {
      // Debounce the estimate calculation
      const timer = setTimeout(() => {
        calculateTimeEstimate();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [service?.id, step]);

  const handleSignature = () => {
    if (signatureRef.current) {
      const signature = signatureRef.current.toDataURL();
      setFormData(prev => ({ ...prev, waiver_signature: signature, waiver_signed: true }));
      setShowWaiver(false);
      toast.success('Waiver signed successfully!');
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const calculateFees = () => {
    if (!service) return { base: 0, addons: 0, mileage: 0, urgency: 0, firstTimeDiscount: 0, couponDiscount: 0, creditsApplied: 0, pickupBudget: 0, total: 0, totalBeforeCredits: 0, distance: 0 };
    
    // For rides, use distance-based pricing instead of hourly
    let base;
    if (service.service_type === 'rides') {
      // Ride pricing: base fare + per mile rate
      const baseFare = 5.00;
      const perMileRate = 2.50;
      const estimatedMiles = formData.mileage_fee ? (formData.mileage_fee / perMileRate) : 15;
      base = baseFare + (estimatedMiles * perMileRate);
    } else {
      base = service.hourly_rate * formData.duration_hours;
    }
    let recurringDiscount = 0;
    if (formData.is_recurring && service.recurring_discount > 0) {
      recurringDiscount = base * (service.recurring_discount / 100);
      base *= (1 - service.recurring_discount / 100);
    }
    
    // Add-ons total
    const addonsTotal = calculateAddonsTotal();
    
    // Pickup budget for errands/tasks (prepay only)
    const pickupBudget = (service?.service_type === 'errands' || service?.service_type === 'tasks') && 
      formData.payment_type === 'pickup_prepay' ? (parseFloat(formData.pickup_budget) || 0) : 0;
    
    // Mileage fee from API calculation
    let mileage = formData.mileage_fee || 0;
    
    // Urgency fee
    let urgency = 0;
    if (formData.urgency_level === 'express') {
      urgency = base * 0.15;
    } else if (formData.urgency_level === 'urgent') {
      urgency = base * 0.25; // 25% for urgent bookings
    }
    
    let subtotal = base + addonsTotal + mileage + urgency + pickupBudget;
    
    // First-time customer discount (10%) - only on service fees, not pickup budget
    let firstTimeDiscountAmount = 0;
    if (isFirstTimeCustomer) {
      const discountableAmount = base + addonsTotal + mileage + urgency;
      firstTimeDiscountAmount = discountableAmount * (firstTimeDiscount / 100);
      subtotal -= firstTimeDiscountAmount;
    }
    
    // Coupon discount
    let couponDiscountAmount = 0;
    if (couponApplied && couponApplied.discount_amount) {
      couponDiscountAmount = couponApplied.discount_amount;
      subtotal -= couponDiscountAmount;
    }
    
    // Total before credits
    const totalBeforeCredits = Math.max(0, subtotal);
    
    // Referral credits applied
    const creditsAppliedAmount = useCredits ? Math.min(creditsToApply, totalBeforeCredits) : 0;
    
    // Final total after credits
    const total = Math.max(0, totalBeforeCredits - creditsAppliedAmount);
    
    return {
      base: base.toFixed(2),
      addons: addonsTotal.toFixed(2),
      recurringDiscount: recurringDiscount.toFixed(2),
      mileage: mileage.toFixed(2),
      urgency: urgency.toFixed(2),
      firstTimeDiscount: firstTimeDiscountAmount.toFixed(2),
      couponDiscount: couponDiscountAmount.toFixed(2),
      creditsApplied: creditsAppliedAmount.toFixed(2),
      pickupBudget: pickupBudget.toFixed(2),
      totalBeforeCredits: totalBeforeCredits.toFixed(2),
      total: total.toFixed(2),
      distance: formData.distance_miles || 0
    };
  };

  const calculateTotal = () => {
    return calculateFees().total;
  };

  const calculateTotalWithoutCredits = () => {
    return calculateFees().totalBeforeCredits;
  };

  const handleCleaningInfoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      cleaning_info: { ...prev.cleaning_info, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate guest info if not logged in
    if (!isAuthenticated && isGuestCheckout) {
      if (!guestInfo.full_name.trim()) {
        toast.error('Please enter your name');
        setStep(1);
        setTimeout(() => document.querySelector('[data-testid="guest-name"]')?.focus(), 100);
        return;
      }
      if (!guestInfo.email.trim() || !guestInfo.email.includes('@')) {
        toast.error('Please enter a valid email address');
        setStep(1);
        setTimeout(() => document.querySelector('[data-testid="guest-email"]')?.focus(), 100);
        return;
      }
      if (!guestInfo.phone.trim()) {
        toast.error('Please enter your phone number');
        setStep(1);
        setTimeout(() => document.querySelector('[data-testid="guest-phone"]')?.focus(), 100);
        return;
      }
    }
    
    // Validate schedule (Step 1)
    if (!formData.scheduled_date) {
      toast.error('Please select a date for your appointment');
      setStep(1);
      setTimeout(() => document.querySelector('[data-testid="booking-date"]')?.focus(), 100);
      return;
    }
    if (!formData.scheduled_time) {
      toast.error('Please select a time for your appointment');
      setStep(1);
      setTimeout(() => document.querySelector('[data-testid="booking-time"]')?.click(), 100);
      return;
    }
    
    // Validate address (Step 2)
    if (!formData.address.trim()) {
      toast.error('Please enter your address');
      setStep(2);
      setTimeout(() => document.querySelector('[data-testid="booking-address"]')?.focus(), 100);
      return;
    }
    if (!formData.city.trim()) {
      toast.error('Please enter your city');
      setStep(2);
      setTimeout(() => document.querySelector('[data-testid="booking-city"]')?.focus(), 100);
      return;
    }
    if (!formData.zip_code.trim()) {
      toast.error('Please enter your ZIP code');
      setStep(2);
      setTimeout(() => document.querySelector('[data-testid="booking-zipcode"]')?.focus(), 100);
      return;
    }
    
    if (!formData.waiver_signed) {
      toast.error('Please sign the waiver to continue');
      setStep(5);
      setTimeout(() => document.querySelector('[data-testid="sign-waiver-btn"]')?.scrollIntoView({ behavior: 'smooth' }), 100);
      return;
    }
    
    if (!formData.non_solicitation_agreed) {
      toast.error('Please agree to the non-solicitation terms to continue');
      setStep(5);
      return;
    }
    
    // Validate service agreement acknowledgments
    if (!formData.service_agreement.service_scope_acknowledged) {
      toast.error('Please acknowledge the service scope limitation');
      setStep(5);
      return;
    }
    
    if (!formData.service_agreement.payment_terms_agreed) {
      toast.error('Please agree to the payment terms');
      setStep(5);
      return;
    }
    
    // Validate pickup budget for prepay errand services
    if ((service?.service_type === 'errands' || service?.service_type === 'tasks') && 
        formData.payment_type === 'pickup_prepay' && 
        (!formData.pickup_budget || parseFloat(formData.pickup_budget) <= 0)) {
      toast.error('Please enter an estimated budget for pickup items');
      setStep(1);
      setTimeout(() => document.querySelector('[data-testid="pickup-budget-input"]')?.focus(), 100);
      return;
    }
    
    // Validate cash payment waiver
    if (formData.payment_method === 'cash' && !formData.cash_payment_waiver_agreed) {
      toast.error('Please agree to the cash payment terms to continue');
      setStep(5);
      setTimeout(() => document.querySelector('[data-testid="cash-waiver-checkbox"]')?.scrollIntoView({ behavior: 'smooth' }), 100);
      return;
    }
    
    setSubmitting(true);
    try {
      // Calculate the fees to get credits applied
      const fees = calculateFees();
      const creditsAppliedAmount = useCredits ? parseFloat(fees.creditsApplied) : 0;
      
      // Prepare add-ons data for all service types
      const addonsData = getSelectedAddonsDetails();
      
      // Sanitize cleaning_info - convert empty strings to null for numeric fields
      const sanitizedCleaningInfo = service?.service_type === 'cleaning' ? {
        ...formData.cleaning_info,
        square_footage: formData.cleaning_info.square_footage ? parseInt(formData.cleaning_info.square_footage) || null : null,
        num_bedrooms: parseInt(formData.cleaning_info.num_bedrooms) || 1,
        num_bathrooms: parseInt(formData.cleaning_info.num_bathrooms) || 1,
        num_floors: parseInt(formData.cleaning_info.num_floors) || 1,
      } : null;
      
      // Only include relevant info based on service type
      const submitData = {
        ...formData,
        // Guest checkout info
        is_guest: !isAuthenticated && isGuestCheckout,
        guest_name: isGuestCheckout ? guestInfo.full_name : null,
        guest_email: isGuestCheckout ? guestInfo.email : null,
        guest_phone: isGuestCheckout ? guestInfo.phone : null,
        client_name: isGuestCheckout ? guestInfo.full_name : (user?.full_name || formData.client_name),
        client_phone: isGuestCheckout ? guestInfo.phone : (user?.phone || formData.client_phone),
        patient_info: null, // Legacy field
        cleaning_info: sanitizedCleaningInfo,
        service_agreement: formData.service_agreement,
        // Include add-ons for all service types
        selected_addons: addonsData,
        addons_total: parseFloat(fees.addons),
        // Include credits info if using credits
        credits_to_apply: creditsAppliedAmount,
        total_after_discounts: parseFloat(fees.totalBeforeCredits),
        total_after_credits: parseFloat(fees.total),
        // Include pickup info for errands/tasks services
        payment_type: (service?.service_type === 'errands' || service?.service_type === 'tasks') ? formData.payment_type : 'standard',
        pickup_budget: formData.payment_type === 'pickup_prepay' ? parseFloat(formData.pickup_budget) || 0 : null,
        pickup_items: (formData.pickup_items || []).filter(item => item.trim() !== ''),
        // Include courier info for courier/delivery services
        courier_info: service?.service_type === 'courier' ? {
          pickup_address: formData.courier_pickup_address,
          pickup_city: formData.courier_pickup_city,
          pickup_state: formData.courier_pickup_state || 'NC',
          pickup_zip: formData.courier_pickup_zip,
          pickup_contact: formData.courier_pickup_contact,
          pickup_phone: formData.courier_pickup_phone,
          package_description: formData.courier_package_description,
          package_weight: formData.courier_package_weight,
          package_count: formData.courier_package_count || 1,
          fragile: formData.courier_fragile || false,
          signature_required: formData.courier_signature_required || false
        } : null
      };
      
      // Call appropriate endpoint based on guest checkout or logged in user
      let booking;
      if (!isAuthenticated && isGuestCheckout) {
        // PAYMENT REQUIRED FOR GUESTS
        // Redirect to payment checkout flow - booking is only created AFTER payment
        try {
          const checkoutData = {
            service_id: service.id,
            scheduled_date: formData.scheduled_date,
            scheduled_time: formData.scheduled_time,
            duration_hours: formData.duration_hours || 2,
            guest: {
              name: formData.guest_name,
              email: formData.guest_email,
              phone: formData.guest_phone,
            },
            address: {
              address: formData.address,
              city: formData.city,
              state: formData.state || 'NC',
              zip_code: formData.zip_code,
            },
            special_instructions: formData.special_instructions,
            selected_addons: formData.selected_addons || [],
            is_ride: service?.service_type === 'rides',
          };
          
          // For rides, add ride locations
          if (service?.service_type === 'rides') {
            checkoutData.ride_locations = {
              pickup_address: formData.pickup_address || formData.address,
              pickup_city: formData.pickup_city || formData.city,
              pickup_zip: formData.pickup_zip || formData.zip_code,
              dropoff_address: formData.dropoff_address,
              dropoff_city: formData.dropoff_city,
              dropoff_zip: formData.dropoff_zip,
            };
            checkoutData.passenger_count = formData.passenger_count || 1;
            checkoutData.vehicle_type = formData.vehicle_type || 'sedan';
          }
          
          // Create Stripe checkout session
          const sessionRes = await api.post('/checkout/session', checkoutData);
          
          if (sessionRes.data?.checkout_url) {
            toast.info('Opening secure payment page...');
            // Redirect to Stripe checkout - handle iframe safely
            try {
              if (window.top !== window.self) {
                window.top.location.href = sessionRes.data.checkout_url;
              } else {
                window.location.href = sessionRes.data.checkout_url;
              }
            } catch (e) {
              window.open(sessionRes.data.checkout_url, '_blank');
            }
            return;
          } else {
            throw new Error('Failed to create payment session');
          }
        } catch (error) {
          console.error('Payment session error:', error);
          toast.error(error.response?.data?.detail || 'Payment is required to complete your booking. Please try again.');
          return;
        }
      } else {
        // Logged in user - use regular booking endpoint
        booking = await bookingsAPI.create(submitData);
      }
      
      // If credits were applied, call the credits API after booking is created
      if (creditsAppliedAmount > 0 && isAuthenticated) {
        try {
          await referralsAPI.applyCredits(booking.data.id, creditsAppliedAmount);
          toast.success(`$${creditsAppliedAmount.toFixed(2)} credits applied!`);
        } catch (creditError) {
          console.error('Failed to apply credits:', creditError);
          // Continue with booking even if credits fail
        }
      }
      
      // If total is 0 (fully covered by credits), mark as paid
      if (parseFloat(fees.total) === 0) {
        toast.success('Booking created! Fully covered by credits.');
        navigate('/customer/dashboard');
      } else if (formData.payment_method === 'cash') {
        // Cash payment - no Stripe redirect needed
        toast.success('Booking created! Please have cash ready when the worker arrives. Payment must be made before service begins.');
        navigate('/customer/dashboard');
      } else {
        toast.success('Booking created! Please complete payment.');
        navigate(`/customer/pay/${booking.data.id}`);
      }
    } catch (error) {
      // Handle validation errors properly
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation error format
        const firstError = errorDetail[0];
        const field = firstError?.loc?.join('.') || 'field';
        const msg = firstError?.msg || 'Validation error';
        toast.error(`${field}: ${msg}`);
      } else if (typeof errorDetail === 'object') {
        toast.error(JSON.stringify(errorDetail) || 'Validation error');
      } else {
        toast.error('Failed to create booking');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-pulse-soft text-[var(--primary)]">Loading...</div>
      </div>
    );
  }

  // Show error state if service failed to load
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="card p-8 text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/15 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-white">Service Not Found</h2>
          <p className="text-[var(--text-muted)] mb-6">
            The service you're looking for doesn't exist or couldn't be loaded.
          </p>
          <Link to="/services">
            <Button className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse Services
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get service-specific waiver based on service type
  const getServiceWaiver = () => {
    if (!waivers || !service) return null;
    
    // Map service types to their specific waivers
    const waiverMap = {
      'cleaning': waivers.cleaning_waiver,
      'laundry': waivers.laundry_waiver,
      'courier': waivers.courier_waiver,
      'errands': waivers.errands_waiver,
      'tasks': waivers.tasks_waiver,
    };
    
    // Return the specific waiver or default to cleaning waiver
    return waiverMap[service.service_type] || waivers.cleaning_waiver;
  };
  
  const waiver = getServiceWaiver();

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-40 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={isAuthenticated ? "/customer/dashboard" : "/services"} className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)]">
            <ArrowLeft className="w-5 h-5" />
            {isAuthenticated ? "Back to Dashboard" : "Back to Services"}
          </Link>
          <span className="font-semibold">Book Service</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Service Summary */}
        <div className="card p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              service.service_type === 'laundry' ? 'bg-[#F4C2C2]/15' :
              service.service_type === 'cleaning' ? 'bg-[#D4AF37]/15' :
              service.service_type === 'courier' ? 'bg-[#D4AF37]/15' :
              service.service_type === 'errands' ? 'bg-[#F4C2C2]/15' :
              'bg-[var(--primary)]/15'
            }`}>
              {service.service_type === 'laundry' ? (
                <svg className="w-7 h-7 text-[#F4C2C2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              ) : service.service_type === 'cleaning' ? (
                <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              ) : service.service_type === 'courier' ? (
                <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
              ) : (
                <svg className="w-7 h-7 text-[#F4C2C2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold font-['Cormorant_Garamond']">{service.name}</h1>
              <p className="text-[var(--text-secondary)] text-sm mt-1">{service.description}</p>
              <div className="flex items-center gap-4 mt-3">
                {service.service_type === 'rides' ? (
                  <span className="text-2xl font-bold text-[var(--primary)]">Distance-based pricing</span>
                ) : (
                  <span className="text-2xl font-bold text-[var(--primary)]">${service.hourly_rate}/hr</span>
                )}
                {service.recurring_discount > 0 && (
                  <span className="badge-success">{service.recurring_discount}% off recurring</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Progress Bar - Hidden when using courier form */}
        {!useCourierForm && (
        <div className="mb-8">
          <div className="flex justify-between items-center max-w-3xl mx-auto">
            {[
              { num: 1, label: 'Schedule', icon: Calendar },
              { num: 2, label: 'Location', icon: MapPin },
              { num: 3, label: 'Details', icon: FileText },
              { num: 4, label: 'Extras', icon: Plus },
              { num: 5, label: 'Confirm', icon: Check }
            ].map((s, idx) => {
              const StepIcon = s.icon;
              const isCompleted = step > s.num;
              const isCurrent = step === s.num;
              const isUpcoming = step < s.num;
              
              return (
                <div key={s.num} className="flex items-center flex-1">
                  <div className="flex flex-col items-center relative">
                    {/* Step Circle */}
                    <div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-300 border-2 ${
                        isCompleted 
                          ? 'bg-[var(--primary)] border-[var(--primary)] text-white' 
                          : isCurrent 
                            ? 'bg-[var(--primary)]/20 border-[var(--primary)] text-[var(--primary)]' 
                            : 'bg-[var(--accent)] border-[var(--border)] text-[var(--text-muted)]'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    {/* Step Label */}
                    <span className={`mt-2 text-xs font-medium transition-colors ${
                      isCompleted || isCurrent ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'
                    }`}>
                      {s.label}
                    </span>
                    {/* Current Step Indicator */}
                    {isCurrent && (
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[var(--primary)] animate-pulse" />
                    )}
                  </div>
                  {/* Connector Line */}
                  {idx < 4 && (
                    <div className="flex-1 mx-2 h-1 rounded-full overflow-hidden bg-[var(--accent)]">
                      <div 
                        className={`h-full bg-[var(--primary)] transition-all duration-500 ${
                          step > s.num ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Current Step Description */}
          <p className="text-center text-sm text-[var(--text-muted)] mt-4">
            {step === 1 && 'Choose your preferred date and time'}
            {step === 2 && 'Enter service location and contact details'}
            {step === 3 && `Provide ${service?.service_type || 'service'}-specific information`}
            {step === 4 && 'Add extra services to enhance your booking'}
            {step === 5 && 'Review your booking and accept terms'}
          </p>
        </div>
        )}

        {/* Courier-Specific Booking Form - Replaces standard flow */}
        {useCourierForm && service?.service_type === 'courier' && (
          <div className="card p-6 mb-8">
            <CourierBookingForm
              service={service}
              formData={formData}
              setFormData={setFormData}
              user={user}
              isSubmitting={submitting}
              onSubmit={() => {
                // After courier form completes, continue to extras step
                setUseCourierForm(false);
                setStep(4);
              }}
              onBack={() => navigate(-1)}
            />
          </div>
        )}

        {/* Standard Booking Form - Hidden when using courier form */}
        {!useCourierForm && (
        <form onSubmit={handleSubmit}>
          {/* Guest Checkout / Login Prompt */}
          {!isAuthenticated && step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 mb-6 border-2 border-[var(--primary)]/30"
            >
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[var(--primary)]" />
                How would you like to book?
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/login', { state: { from: `/customer/book/${serviceId}` } })}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    !isGuestCheckout ? 'border-[var(--primary)] bg-[var(--primary)]/5' : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                  }`}
                >
                  <div className="font-semibold mb-1">Sign In / Create Account</div>
                  <p className="text-sm text-[var(--text-muted)]">Track bookings, earn rewards, get discounts</p>
                </button>
                <button
                  type="button"
                  onClick={() => setIsGuestCheckout(true)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isGuestCheckout ? 'border-[var(--primary)] bg-[var(--primary)]/5' : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                  }`}
                >
                  <div className="font-semibold mb-1">Continue as Guest</div>
                  <p className="text-sm text-[var(--text-muted)]">Book quickly without an account</p>
                </button>
              </div>
              
              {/* Guest Info Form */}
              {isGuestCheckout && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-6 pt-6 border-t border-[var(--border)]"
                >
                  <h4 className="font-semibold mb-4">Your Contact Information</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        type="text"
                        value={guestInfo.full_name}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, full_name: e.target.value }))}
                        placeholder="John Doe"
                        className="input-styled mt-1"
                        required
                        data-testid="guest-name"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="john@example.com"
                        className="input-styled mt-1"
                        required
                        data-testid="guest-email"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input
                        type="tel"
                        value={guestInfo.phone}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        className="input-styled mt-1"
                        required
                        data-testid="guest-phone"
                      />
                    </div>
                  </div>
                  
                  {/* Remember Me Checkbox */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[var(--border-primary)]">
                    <Checkbox
                      id="remember-guest"
                      checked={rememberGuestInfo}
                      onCheckedChange={setRememberGuestInfo}
                      className="border-[var(--primary)] data-[state=checked]:bg-[var(--primary)]"
                      data-testid="remember-guest-checkbox"
                    />
                    <label 
                      htmlFor="remember-guest" 
                      className="text-sm text-[var(--text-secondary)] cursor-pointer"
                    >
                      Remember my info for faster checkout next time
                    </label>
                  </div>
                  {guestInfo.full_name && guestInfo.email && (
                    <p className="text-xs text-[var(--text-muted)] mt-2 flex items-center gap-1">
                      <Check className="w-3 h-3 text-[var(--success)]" />
                      Welcome back! Your info has been prefilled.
                    </p>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Step 1: Schedule */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card p-8"
            >
              <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                <Calendar className="w-5 h-5 text-[var(--primary)]" />
                Schedule Your Appointment
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    name="scheduled_date"
                    value={formData.scheduled_date}
                    onChange={handleChange}
                    min={getMinDate()}
                    className="input-styled mt-1"
                    required
                    data-testid="booking-date"
                  />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Select 
                    value={formData.scheduled_time} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, scheduled_time: value }))}
                  >
                    <SelectTrigger className="input-styled mt-1" data-testid="booking-time">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {service?.service_type !== 'rides' && (
                <div>
                  <Label>Duration (hours) *</Label>
                  <Select 
                    value={String(formData.duration_hours)} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, duration_hours: Number(value) }))}
                  >
                    <SelectTrigger className="input-styled mt-1" data-testid="booking-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(h => (
                        <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>

              <div className="mt-6 p-4 bg-[var(--accent)] rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked, recurring_frequency: checked ? 'weekly' : '' }))}
                    data-testid="recurring-checkbox"
                  />
                  <div>
                    <span className="font-semibold">Make this a recurring booking</span>
                    {service.recurring_discount > 0 && (
                      <span className="text-[var(--success)] ml-2 text-sm">Save {service.recurring_discount}%!</span>
                    )}
                  </div>
                </label>
                
                {formData.is_recurring && (
                  <div className="mt-4 space-y-4 pt-4 border-t border-[var(--border-primary)]">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Frequency *</Label>
                        <Select 
                          value={formData.recurring_frequency} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, recurring_frequency: value }))}
                        >
                          <SelectTrigger className="input-styled mt-1 bg-[var(--background-paper)]" data-testid="recurring-frequency">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="bi_weekly">Every 2 Weeks</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>End Date (Optional)</Label>
                        <Input
                          type="date"
                          value={formData.recurring_end_date || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                          className="input-styled mt-1 bg-[var(--background-paper)]"
                          min={formData.scheduled_date}
                          data-testid="recurring-end-date"
                        />
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                          Leave blank for ongoing service
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[var(--background-paper)] rounded-lg border border-[var(--success)] text-sm">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[var(--success)] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-[var(--success)]">Recurring Booking Benefits</p>
                          <ul className="text-[var(--text-secondary)] mt-1 space-y-1">
                            <li>• {service.recurring_discount}% discount on every booking</li>
                            <li>• Same worker assigned when available</li>
                            <li>• Automatic scheduling - no need to rebook</li>
                            <li>• Cancel or pause anytime from your dashboard</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Urgency Level */}
              <div className="mt-6">
                <Label className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-[var(--warning)]" />
                  Booking Urgency
                </Label>
                
                {/* 3-Day Notice Warning */}
                {isWithin3Days && (
                  <div className="mb-4 p-4 bg-[var(--warning)]/10 border-2 border-amber-300 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-amber-800">Urgent Booking Required</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Bookings within 3 days of the service date require urgent scheduling. 
                          A 25% urgency fee has been automatically applied to ensure we can accommodate your request.
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          💡 Tip: Book 3+ days in advance to avoid the urgency fee.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'standard', label: 'Standard', desc: '3+ days notice', fee: 'No extra fee' },
                    { value: 'express', label: 'Express', desc: '24-48 hour notice', fee: '+15%' },
                    { value: 'urgent', label: 'Urgent', desc: 'Within 3 days', fee: '+25%' }
                  ].map(opt => {
                    // Disable standard and express if within 3 days
                    const isDisabled = isWithin3Days && opt.value !== 'urgent';
                    
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => !isDisabled && setFormData(prev => ({ ...prev, urgency_level: opt.value }))}
                        disabled={isDisabled}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          isDisabled 
                            ? 'border-[var(--border-primary)] bg-[var(--background-subtle)] opacity-50 cursor-not-allowed'
                            : formData.urgency_level === opt.value 
                              ? 'border-[var(--primary)] bg-[var(--accent)]' 
                              : 'border-[var(--border-primary)] hover:border-[var(--primary)]/50'
                        }`}
                      >
                        <p className="font-semibold">{opt.label}</p>
                        <p className="text-xs text-[var(--text-muted)]">{opt.desc}</p>
                        <p className={`text-xs mt-1 ${opt.value !== 'standard' ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>
                          {opt.fee}
                        </p>
                        {isDisabled && (
                          <p className="text-xs text-[var(--error)] mt-1">Not available</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Distance for mileage fee - Now auto-calculated */}
              {mileageInfo && (
                <div className="mt-6 p-4 bg-[var(--background-subtle)] rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Car className="w-5 h-5 text-[var(--primary)]" />
                      <span className="font-semibold">Distance from 398 Tolbert Place, Princeville NC</span>
                    </div>
                    <span className="font-bold text-lg">{mileageInfo.distance_miles} miles</span>
                  </div>
                  <p className="text-sm text-[var(--text-muted)] mt-2">
                    {mileageInfo.distance_miles <= 15 
                      ? 'No mileage fee - within free 15-mile service area!' 
                      : `Mileage fee: $${mileageInfo.mileage_fee.toFixed(2)} ($0.65/mile after first 15 miles)`
                    }
                  </p>
                </div>
              )}

              {/* Pickup Payment Section for Errands/Tasks Services */}
              {(service?.service_type === 'errands' || service?.service_type === 'tasks') && (
                <div className="mt-8 p-6 bg-[#D4AF37]/10 rounded-xl border-2 border-[#D4AF37]/30">
                  <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
                    <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
                    Pickup Payment Options
                  </h3>
                  <p className="text-sm text-[var(--text-muted)] mb-4">
                    How would you like to handle payment for items that need to be picked up?
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Option 1: Pay Upfront */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, payment_type: 'pickup_prepay' }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.payment_type === 'pickup_prepay'
                          ? 'border-[#D4AF37] bg-[var(--background-paper)] shadow-md'
                          : 'border-[#D4AF37]/30 bg-[#D4AF37]/10/50 hover:border-blue-300'
                      }`}
                      data-testid="pickup-prepay-option"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.payment_type === 'pickup_prepay' ? 'bg-[#D4AF37]/100 text-white' : 'bg-blue-200 text-[#D4AF37]'
                        }`}>
                          <DollarSign className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold">Pay Upfront</p>
                          <p className="text-xs text-[var(--text-muted)]">For items to be purchased</p>
                        </div>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-2">
                        Give an estimated budget upfront. Worker buys items and uploads receipt. 
                        Any unused amount is refunded to you.
                      </p>
                      {formData.payment_type === 'pickup_prepay' && (
                        <div className="mt-3 flex items-center gap-1 text-[#D4AF37] text-sm">
                          <Check className="w-4 h-4" /> Selected
                        </div>
                      )}
                    </button>

                    {/* Option 2: Already Paid */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, payment_type: 'pickup_paid', pickup_budget: '' }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.payment_type === 'pickup_paid'
                          ? 'border-[#D4AF37] bg-[var(--background-paper)] shadow-md'
                          : 'border-[#D4AF37]/30 bg-[#D4AF37]/10/50 hover:border-blue-300'
                      }`}
                      data-testid="pickup-paid-option"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.payment_type === 'pickup_paid' ? 'bg-[#D4AF37]/100 text-white' : 'bg-blue-200 text-[#D4AF37]'
                        }`}>
                          <CreditCard className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold">Already Paid</p>
                          <p className="text-xs text-[var(--text-muted)]">For pre-paid orders</p>
                        </div>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-2">
                        Items are already paid for (online order, prescription, etc.). 
                        Worker just picks them up - no additional item cost.
                      </p>
                      {formData.payment_type === 'pickup_paid' && (
                        <div className="mt-3 flex items-center gap-1 text-[#D4AF37] text-sm">
                          <Check className="w-4 h-4" /> Selected
                        </div>
                      )}
                    </button>
                  </div>

                  {/* Budget Input for Prepay */}
                  {formData.payment_type === 'pickup_prepay' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 p-4 bg-[var(--background-paper)] rounded-lg border border-[#D4AF37]/30"
                    >
                      <Label className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                        Estimated Budget for Items *
                      </Label>
                      <div className="relative mt-2">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">$</span>
                        <Input
                          type="number"
                          value={formData.pickup_budget}
                          onChange={(e) => setFormData(prev => ({ ...prev, pickup_budget: e.target.value }))}
                          placeholder="50.00"
                          min="1"
                          step="0.01"
                          className="pl-8"
                          data-testid="pickup-budget-input"
                        />
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-2">
                        This amount will be charged along with the service fee. 
                        Worker will upload receipt after purchase, and any difference will be refunded or charged accordingly.
                      </p>
                    </motion.div>
                  )}

                  {/* Shopping List */}
                  <div className="mt-4 p-4 bg-[var(--background-paper)] rounded-lg border border-[#D4AF37]/30">
                    <Label className="flex items-center gap-2 mb-3">
                      <Receipt className="w-4 h-4 text-[#D4AF37]" />
                      What needs to be picked up? <span className="text-[var(--text-muted)]">(Optional)</span>
                    </Label>
                    <div className="space-y-2">
                      {(formData.pickup_items || []).map((item, index) => (
                        <div key={`pickup-item-${index}-${item.substring(0, 10)}`} className="flex items-center gap-2">
                          <Input
                            value={item}
                            onChange={(e) => {
                              const updated = [...(formData.pickup_items || [])];
                              updated[index] = e.target.value;
                              setFormData(prev => ({ ...prev, pickup_items: updated }));
                            }}
                            placeholder={`Item ${index + 1}`}
                            className="flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (formData.pickup_items || []).filter((_, i) => i !== index);
                              setFormData(prev => ({ ...prev, pickup_items: updated }));
                            }}
                            className="p-2 text-[var(--error)] hover:bg-[var(--error)]/10 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        pickup_items: [...(prev.pickup_items || []), ''] 
                      }))}
                      className="mt-2 text-[#D4AF37] border-blue-300"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                </div>
              )}

              {/* Courier/Delivery Specific Section */}
              {service?.service_type === 'courier' && (
                <div className="mt-6 p-5 bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-xl border border-[#D4AF37]/30">
                  <h3 className="font-semibold flex items-center gap-2 text-lg mb-4">
                    <Truck className="w-5 h-5 text-[#D4AF37]" />
                    Pickup & Package Details
                    <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full ml-auto">
                      Required
                    </span>
                  </h3>
                  
                  {/* Pickup Location */}
                  <div className="space-y-4 mb-6">
                    <div className="p-4 bg-[var(--background-paper)] rounded-lg border border-[var(--border-primary)]">
                      <Label className="flex items-center gap-2 text-blue-400 font-semibold mb-3">
                        <MapPin className="w-4 h-4" />
                        Pickup Location (Where we collect the package)
                      </Label>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm text-[var(--text-muted)]">Pickup Address *</Label>
                          <Input
                            value={formData.courier_pickup_address || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_address: e.target.value }))}
                            placeholder="123 Main St, Suite 100"
                            className="input-styled mt-1"
                            data-testid="courier-pickup-address"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-sm text-[var(--text-muted)]">City *</Label>
                            <Input
                              value={formData.courier_pickup_city || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_city: e.target.value }))}
                              placeholder="Tarboro"
                              className="input-styled mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-[var(--text-muted)]">State</Label>
                            <Input
                              value={formData.courier_pickup_state || 'NC'}
                              onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_state: e.target.value }))}
                              className="input-styled mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-[var(--text-muted)]">ZIP *</Label>
                            <Input
                              value={formData.courier_pickup_zip || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_zip: e.target.value }))}
                              placeholder="27886"
                              className="input-styled mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm text-[var(--text-muted)]">Pickup Contact Name</Label>
                          <Input
                            value={formData.courier_pickup_contact || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_contact: e.target.value }))}
                            placeholder="Contact person at pickup"
                            className="input-styled mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-[var(--text-muted)]">Pickup Phone</Label>
                          <Input
                            value={formData.courier_pickup_phone || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_phone: e.target.value }))}
                            placeholder="(555) 123-4567"
                            className="input-styled mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Package Details */}
                  <div className="p-4 bg-[var(--background-paper)] rounded-lg border border-[var(--border-primary)] mb-4">
                    <Label className="flex items-center gap-2 text-[#D4AF37] font-semibold mb-3">
                      <Package className="w-4 h-4" />
                      Package Details
                    </Label>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm text-[var(--text-muted)]">Package Description *</Label>
                        <Textarea
                          value={formData.courier_package_description || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, courier_package_description: e.target.value }))}
                          placeholder="Describe the package(s) - size, weight, contents, etc."
                          className="input-styled mt-1"
                          rows={2}
                          data-testid="courier-package-description"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm text-[var(--text-muted)]">Estimated Weight</Label>
                          <Input
                            value={formData.courier_package_weight || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, courier_package_weight: e.target.value }))}
                            placeholder="e.g., 5 lbs"
                            className="input-styled mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-[var(--text-muted)]">Number of Items</Label>
                          <Input
                            type="number"
                            value={formData.courier_package_count || 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, courier_package_count: parseInt(e.target.value) || 1 }))}
                            min="1"
                            className="input-styled mt-1"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <Checkbox
                          checked={formData.courier_fragile || false}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, courier_fragile: checked }))}
                          id="courier-fragile"
                        />
                        <Label htmlFor="courier-fragile" className="text-sm cursor-pointer">
                          Package is fragile - handle with care
                        </Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={formData.courier_signature_required || false}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, courier_signature_required: checked }))}
                          id="courier-signature"
                        />
                        <Label htmlFor="courier-signature" className="text-sm cursor-pointer">
                          Signature required on delivery
                        </Label>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-[var(--text-muted)] mt-3">
                    <strong>Note:</strong> Delivery address will be entered in the next step. 
                    This form will be printed for the courier driver.
                  </p>
                </div>
              )}

              <Button 
                type="button" 
                onClick={() => setStep(2)} 
                className="btn-primary w-full mt-8"
                disabled={!formData.scheduled_date || !formData.scheduled_time}
                data-testid="next-step-1"
              >
                Continue <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          )}

          {/* Step 2: Location & Contact */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card p-8"
            >
              <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                <MapPin className="w-5 h-5 text-[var(--primary)]" />
                {service?.service_type === 'courier' ? 'Delivery Destination' : 'Service Location & Contact'}
              </h2>

              {service?.service_type === 'courier' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                  <p className="text-sm text-green-800">
                    <strong>This is where the package will be delivered.</strong> You&apos;ll enter pickup details in the next section.
                  </p>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <Label>Street Address *</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-styled mt-1"
                    required
                    data-testid="booking-address"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                      data-testid="booking-city"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Input
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>ZIP Code *</Label>
                    <div className="relative">
                      <Input
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleChange}
                        className="input-styled mt-1"
                        required
                        maxLength={5}
                        data-testid="booking-zipcode"
                      />
                      {calculatingMileage && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {mileageInfo && (
                      <p className="text-xs text-[var(--success)] mt-1 flex items-center gap-1">
                        <Car className="w-3 h-3" />
                        {mileageInfo.distance_miles} miles from our location
                        {mileageInfo.mileage_fee > 0 && ` (+$${mileageInfo.mileage_fee.toFixed(2)} fee)`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Name *</Label>
                    <Input
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>Contact Phone *</Label>
                    <Input
                      name="client_phone"
                      type="tel"
                      value={formData.client_phone}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Emergency Contact Name</Label>
                    <Input
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleChange}
                      className="input-styled mt-1"
                    />
                  </div>
                  <div>
                    <Label>Emergency Contact Phone</Label>
                    <Input
                      name="emergency_contact_phone"
                      type="tel"
                      value={formData.emergency_contact_phone}
                      onChange={handleChange}
                      className="input-styled mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Special Instructions</Label>
                  <Textarea
                    name="special_instructions"
                    value={formData.special_instructions}
                    onChange={handleChange}
                    className="input-styled mt-1"
                    rows={3}
                    placeholder="Any special requests or information we should know..."
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1">
                  Back
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setStep(3)} 
                  className="btn-primary flex-1"
                  disabled={!formData.address || !formData.city || !formData.state || !formData.zip_code}
                  data-testid="next-step-2"
                >
                  Continue <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Service-Specific Details */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card p-8"
            >
              <h2 className="text-xl font-bold flex items-center gap-2 mb-2">
                <Home className="w-5 h-5 text-[var(--secondary)]" />
                {service?.service_type === 'cleaning' && 'Property Details'}
                {service?.service_type === 'laundry' && 'Laundry Details'}
                {service?.service_type === 'courier' && 'Drop-off Details'}
                {service?.service_type === 'errands' && 'Errand Details'}
                {service?.service_type === 'tasks' && 'Task Details'}
              </h2>
              <p className="text-[var(--text-secondary)] mb-6">
                {service?.service_type === 'cleaning' && 'Tell us about the space to be cleaned'}
                {service?.service_type === 'laundry' && 'Tell us about your laundry needs'}
                {service?.service_type === 'courier' && 'Confirm where the package will be delivered'}
                {service?.service_type === 'errands' && 'Tell us about the errands you need run'}
                {service?.service_type === 'tasks' && 'Tell us about the tasks you need help with'}
              </p>
              
              <ServiceDetailForms 
                serviceType={service?.service_type}
                data={formData}
                onChange={(updatedData) => setFormData(prev => ({ ...prev, ...updatedData }))}
              />

              {/* Live Time Estimate Preview */}
              {timeEstimate && (
                <div className="mt-6">
                  <TimeEstimateDisplay 
                    estimate={timeEstimate}
                    isLoading={estimatingTime}
                    error={timeEstimateError}
                    showBreakdown={false}
                    compact={true}
                  />
                </div>
              )}

              <div className="flex gap-4 mt-8">
                <Button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">
                  Back
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setStep(4)} 
                  className="btn-primary flex-1"
                  data-testid="next-step-3"
                >
                  Continue to Extras <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Add-ons / Extras - Pre-Payment Upsell */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card p-8"
            >
              <h2 className="text-xl font-bold flex items-center gap-2 mb-2">
                <Plus className="w-5 h-5 text-[#D4AF37]" />
                Enhance Your Booking
              </h2>
              <p className="text-[var(--text-muted)] mb-6">
                Would you like to add any extra services to your {service?.service_type || 'booking'}?
              </p>

              {/* Add-ons Section */}
              {availableAddons.length > 0 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableAddons.map((addon) => {
                      const isSelected = selectedAddons[addon.id];
                      const quantity = selectedAddons[addon.id] || 0;
                      const maxQty = addon.max_quantity || 1;
                      
                      return (
                        <div 
                          key={addon.id}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            isSelected 
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-lg' 
                              : 'border-gray-600 bg-[var(--background-paper)] hover:border-[#D4AF37]/50 hover:bg-[var(--background-paper)]/80'
                          }`}
                          onClick={() => !isSelected && handleAddonToggle(addon.id)}
                          data-testid={`addon-${addon.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Checkbox 
                                  checked={!!isSelected}
                                  onCheckedChange={() => handleAddonToggle(addon.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37]"
                                />
                                <span className="font-semibold text-white text-lg">
                                  {addon.name}
                                </span>
                              </div>
                              {addon.description && (
                                <p className="text-sm text-gray-400 mt-2 ml-6">
                                  {addon.description}
                                </p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="font-bold text-[#D4AF37] text-xl">
                                {formatAddonPrice(addon)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Quantity controls */}
                          {isSelected && maxQty > 1 && (
                            <div className="flex items-center justify-between mt-4 ml-6 pt-3 border-t border-gray-600">
                              <span className="text-sm text-gray-400">Quantity:</span>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleAddonQuantity(addon.id, -1); }}
                                  className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center text-white"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-10 text-center font-bold text-white text-lg">{quantity}</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleAddonQuantity(addon.id, 1); }}
                                  className="w-8 h-8 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center text-white"
                                  disabled={quantity >= maxQty}
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                          {isSelected && maxQty === 1 && (
                            <div className="mt-3 ml-6 flex items-center gap-2 text-[#D4AF37]">
                              <Check className="w-5 h-5" />
                              <span className="font-medium">Added to your booking</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Selected Add-ons Summary */}
                  {Object.keys(selectedAddons).length > 0 && (
                    <div className="p-4 bg-emerald-900/20 rounded-xl border border-emerald-500/30">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-emerald-400">
                          {Object.keys(selectedAddons).length} Extra Service{Object.keys(selectedAddons).length > 1 ? 's' : ''} Added
                        </span>
                        <span className="font-bold text-emerald-400 text-xl">
                          +${calculateAddonsTotal().toFixed(2)}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-emerald-300/70">
                        {getSelectedAddonsDetails().map((addon, idx) => (
                          <span key={addon.add_on_id}>
                            {addon.name}{addon.quantity > 1 ? ` x${addon.quantity}` : ''}
                            {idx < getSelectedAddonsDetails().length - 1 ? ' • ' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 bg-[var(--background-subtle)] rounded-xl">
                  <ShoppingBag className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
                  <p className="text-[var(--text-muted)]">No extra services available for this booking type.</p>
                  <p className="text-sm text-[var(--text-muted)] mt-2">Continue to review and confirm your booking.</p>
                </div>
              )}

              <div className="flex gap-4 mt-8">
                <Button type="button" onClick={() => setStep(3)} className="btn-secondary flex-1">
                  Back
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setStep(5)} 
                  className="btn-primary flex-1"
                  data-testid="next-step-4"
                >
                  {Object.keys(selectedAddons).length > 0 
                    ? `Continue with Extras (+$${calculateAddonsTotal().toFixed(2)})` 
                    : 'Skip & Continue'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Waiver & Confirm */}
          {step === 5 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card p-8"
            >
              <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                <FileText className="w-5 h-5 text-[var(--primary)]" />
                Review & Sign Waiver
              </h2>

              {/* NON-MEDICAL SERVICES DISCLAIMER - REQUIRED */}
              <NonMedicalDisclaimer 
                variant="waiver" 
                showCheckbox={true}
                onAccept={(checked) => setFormData(prev => ({ ...prev, non_medical_acknowledged: checked }))}
              />

              {/* Order Summary with Fee Breakdown */}
              <div className="bg-[var(--background-subtle)] rounded-xl p-6 mb-6">
                <h3 className="font-semibold mb-4">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Service:</span>
                    <span className="font-medium">{service.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Date:</span>
                    <span className="font-medium">{formData.scheduled_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Time:</span>
                    <span className="font-medium">{formData.scheduled_time}</span>
                  </div>
                  {service?.service_type !== 'rides' && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">Duration:</span>
                      <span className="font-medium">{formData.duration_hours} hour(s)</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Urgency:</span>
                    <span className="font-medium capitalize">{formData.urgency_level}</span>
                  </div>
                </div>
                
                {/* Time Estimate Display */}
                <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
                  <TimeEstimateDisplay 
                    estimate={timeEstimate}
                    isLoading={estimatingTime}
                    error={timeEstimateError}
                    showBreakdown={true}
                    compact={false}
                  />
                </div>
                
                <div className="border-t border-[var(--border-primary)] pt-3 mt-3">
                  <h4 className="font-semibold mb-2">Fee Breakdown</h4>
                  <div className="space-y-2">
                    {service.service_type === 'rides' ? (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Ride Fare:</span>
                        <span className="font-medium">${calculateFees().base?.toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Base ({formData.duration_hours}h × ${service.hourly_rate}):</span>
                        <span className="font-medium">${calculateFees().base?.toFixed(2)}</span>
                      </div>
                    )}
                    {formData.is_recurring && service.recurring_discount > 0 && (
                      <div className="flex justify-between text-[var(--success)]">
                        <span>Recurring Discount:</span>
                        <span>-{service.recurring_discount}%</span>
                      </div>
                    )}
                    
                    {/* Add-ons */}
                    {parseFloat(calculateFees().addons) > 0 && (
                      <div className="mt-2 pt-2 border-t border-emerald-200">
                        <div className="flex justify-between text-emerald-700">
                          <span className="flex items-center gap-1">
                            <Plus className="w-3 h-3" />
                            Add-Ons ({Object.keys(selectedAddons).length}):
                          </span>
                          <span className="font-medium">${calculateFees().addons}</span>
                        </div>
                        <div className="text-xs text-[var(--text-muted)] mt-1 ml-4">
                          {getSelectedAddonsDetails().map((addon, idx) => (
                            <div key={addon.add_on_id} className="flex justify-between">
                              <span>{addon.name}{addon.quantity > 1 ? ` x${addon.quantity}` : ''}</span>
                              <span>${addon.total.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {parseFloat(calculateFees().mileage) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Mileage Fee:</span>
                        <span className="font-medium">${calculateFees().mileage}</span>
                      </div>
                    )}
                    {parseFloat(calculateFees().urgency) > 0 && (
                      <div className="flex justify-between text-[var(--warning)]">
                        <span>Urgency Fee ({formData.urgency_level === 'express' ? '15%' : '30%'}):</span>
                        <span className="font-medium">${calculateFees().urgency}</span>
                      </div>
                    )}
                    
                    {/* Pickup Budget for Errands */}
                    {parseFloat(calculateFees().pickupBudget) > 0 && (
                      <div className="flex justify-between text-[#D4AF37] bg-[#D4AF37]/10 -mx-2 px-2 py-1 rounded">
                        <span className="flex items-center gap-1">
                          <ShoppingBag className="w-4 h-4" />
                          Pickup Budget (Refundable):
                        </span>
                        <span className="font-medium">${calculateFees().pickupBudget}</span>
                      </div>
                    )}
                    
                    {/* First-time Customer Discount */}
                    {isFirstTimeCustomer && parseFloat(calculateFees().firstTimeDiscount) > 0 && (
                      <div className="flex justify-between text-[var(--success)] bg-[var(--success)]/10 -mx-2 px-2 py-1 rounded">
                        <span className="flex items-center gap-1">
                          <Gift className="w-4 h-4" />
                          First-Time Discount (10%):
                        </span>
                        <span className="font-medium">-${calculateFees().firstTimeDiscount}</span>
                      </div>
                    )}
                    
                    {/* Coupon Discount */}
                    {couponApplied && parseFloat(calculateFees().couponDiscount) > 0 && (
                      <div className="flex justify-between text-[#F4C2C2] bg-[#F4C2C2]/10 -mx-2 px-2 py-1 rounded">
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          Coupon ({couponApplied.discount_type === 'percentage' ? `${couponApplied.discount_value}%` : `$${couponApplied.discount_value}`}):
                        </span>
                        <span className="font-medium">-${calculateFees().couponDiscount}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Coupon Code Input */}
                  <div className="border-t border-[var(--border-primary)] pt-3 mt-3">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Have a Coupon Code?
                    </h4>
                    {!couponApplied ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="flex-1 uppercase"
                          data-testid="coupon-code-input"
                        />
                        <Button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="btn-secondary"
                          data-testid="apply-coupon-btn"
                        >
                          {couponLoading ? 'Checking...' : 'Apply'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-[#F4C2C2]/10 border border-[#F4C2C2]/30 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Tag className="w-5 h-5 text-[#F4C2C2]" />
                          <span className="font-medium text-purple-800">{couponCode}</span>
                          <span className="text-sm text-[#F4C2C2]">applied</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveCoupon}
                          className="text-[#F4C2C2] hover:text-purple-800 p-1"
                          data-testid="remove-coupon-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Referral Credits Section */}
                  {referralCredits.has_credits && (
                    <div className="border-t border-[var(--border-primary)] pt-3 mt-3">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Gift className="w-4 h-4 text-[var(--success)]" />
                        Referral Credits Available
                      </h4>
                      <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[var(--success)]">
                            Available: <strong>${referralCredits.total_available?.toFixed(2) || '0.00'}</strong>
                          </span>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={useCredits}
                              onCheckedChange={handleToggleCredits}
                              data-testid="use-credits-checkbox"
                            />
                            <span className="text-sm font-medium text-green-700">Apply Credits</span>
                          </label>
                        </div>
                        {useCredits && (
                          <div className="mt-2 pt-2 border-t border-[var(--success)]/30">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-green-700">Credits to apply:</span>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max={Math.min(referralCredits.total_available, parseFloat(calculateTotalWithoutCredits()))}
                                  step="0.01"
                                  value={creditsToApply}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    const max = Math.min(referralCredits.total_available, parseFloat(calculateTotalWithoutCredits()));
                                    setCreditsToApply(Math.min(val, max));
                                  }}
                                  className="w-24 h-8 text-sm text-right"
                                  data-testid="credits-amount-input"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const max = Math.min(referralCredits.total_available, parseFloat(calculateTotalWithoutCredits()));
                                    setCreditsToApply(max);
                                  }}
                                  className="text-xs text-[var(--success)] hover:text-[var(--success)] underline"
                                >
                                  Use Max
                                </button>
                              </div>
                            </div>
                            <p className="text-xs text-[var(--success)] mt-1">
                              Remaining balance after booking: ${(referralCredits.total_available - creditsToApply).toFixed(2)}
                            </p>
                          </div>
                        )}
                        {referralCredits.referral_discount > 0 && (
                          <p className="text-xs text-[var(--success)] mt-2">
                            Includes ${referralCredits.referral_discount.toFixed(2)} welcome discount from referral
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="border-t border-[var(--border-primary)] pt-2 mt-2">
                    {/* Show credits applied in breakdown */}
                    {useCredits && parseFloat(calculateFees().creditsApplied) > 0 && (
                      <div className="flex justify-between text-sm mb-2 text-[var(--success)]">
                        <span className="flex items-center gap-1">
                          <Gift className="w-3 h-3" />
                          Referral Credits Applied:
                        </span>
                        <span className="font-medium">-${calculateFees().creditsApplied}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total (Payment Required):</span>
                      <span className="text-[var(--primary)]">${calculateTotal()}</span>
                    </div>
                    {(isFirstTimeCustomer || couponApplied || (useCredits && parseFloat(calculateFees().creditsApplied) > 0)) && (
                      <p className="text-xs text-[var(--success)] mt-1 flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        You are saving ${(parseFloat(calculateFees().firstTimeDiscount) + parseFloat(calculateFees().couponDiscount) + parseFloat(calculateFees().creditsApplied)).toFixed(2)}!
                      </p>
                    )}
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      {parseFloat(calculateTotal()) === 0 ? 'Your booking is fully covered by credits!' : 'Full payment is required to confirm your booking'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="border border-[var(--border-primary)] rounded-xl p-6 mb-6">
                <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-[var(--primary)]" />
                  Payment Method
                </h3>
                <p className="text-sm text-[var(--text-muted)] mb-4">
                  Select how you would like to pay for this service
                </p>
                
                {/* Guest Warning - Show only for guests */}
                {isGuestCheckout && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-sm text-amber-400">
                      <strong>Guest checkout:</strong> Online payment required. <Link to="/register" className="underline hover:text-amber-300">Create an account</Link> for cash payment option.
                    </p>
                  </div>
                )}
                
                <div className={`grid gap-4 ${!isGuestCheckout ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                  {/* Stripe/Card Payment Option */}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, payment_method: 'stripe', cash_payment_waiver_agreed: false }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.payment_method === 'stripe'
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5 shadow-md'
                        : 'border-[var(--border-primary)] hover:border-[var(--primary)]/50'
                    }`}
                    data-testid="payment-method-stripe"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        formData.payment_method === 'stripe' ? 'bg-[var(--primary)] text-white' : 'bg-[var(--accent)] text-[var(--text-muted)]'
                      }`}>
                        <CreditCard className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold">Card Payment</p>
                        <p className="text-xs text-[var(--text-muted)]">Pay securely online</p>
                      </div>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-2">
                      Pay now with credit/debit card via Stripe. Your booking will be confirmed instantly.
                    </p>
                    {formData.payment_method === 'stripe' && (
                      <div className="mt-3 flex items-center gap-1 text-[var(--primary)] text-sm">
                        <Check className="w-4 h-4" /> Selected
                      </div>
                    )}
                  </button>

                  {/* Cash Payment Option - Only for authenticated users */}
                  {!isGuestCheckout && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, payment_method: 'cash' }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.payment_method === 'cash'
                          ? 'border-[var(--success)] bg-[var(--success)]/5 shadow-md'
                          : 'border-[var(--border-primary)] hover:border-[var(--success)]/50'
                      }`}
                      data-testid="payment-method-cash"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.payment_method === 'cash' ? 'bg-[var(--success)] text-white' : 'bg-[var(--accent)] text-[var(--text-muted)]'
                        }`}>
                          <DollarSign className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold">Cash Payment</p>
                          <p className="text-xs text-[var(--text-muted)]">Pay in person</p>
                        </div>
                      </div>
                      <p className="text-sm text-[var(--text-secondary)] mt-2">
                        Pay cash directly to the worker before service begins. Payment must be verified.
                      </p>
                      {formData.payment_method === 'cash' && (
                        <div className="mt-3 flex items-center gap-1 text-[var(--success)] text-sm">
                          <Check className="w-4 h-4" /> Selected
                        </div>
                      )}
                    </button>
                  )}
                </div>

                {/* Cash Payment Waiver - Required for cash payments */}
                {formData.payment_method === 'cash' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-6 p-5 bg-amber-50 border-2 border-amber-300 rounded-xl"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-amber-800 text-lg">Cash Payment Terms & Conditions</h4>
                        <p className="text-sm text-amber-700 mt-1">You must agree to these terms to proceed with cash payment</p>
                      </div>
                    </div>
                    
                    <div className="bg-[var(--background-paper)] rounded-lg p-4 mb-4 border border-amber-500/30">
                      <h5 className="font-semibold text-amber-900 mb-3">IMPORTANT - Please Read Carefully:</h5>
                      <ul className="space-y-2 text-sm text-amber-800">
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-amber-600">1.</span>
                          <span><strong>Payment Before Service:</strong> Full cash payment of <strong>${calculateTotal()}</strong> must be collected by the worker BEFORE any service begins.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-amber-600">2.</span>
                          <span><strong>Worker Verification Required:</strong> The assigned worker must verify receipt of payment in the app before starting work.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-amber-600">3.</span>
                          <span><strong>No Payment = No Service:</strong> If cash payment is not received and verified when the worker arrives, the booking will be automatically cancelled and marked as &quot;Not Paid&quot;.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-amber-600">4.</span>
                          <span><strong>Exact Amount Required:</strong> Please have the exact amount ready. Workers may not carry change.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-amber-600">5.</span>
                          <span><strong>Receipt Provided:</strong> A digital receipt will be sent to your email once payment is verified.</span>
                        </li>
                      </ul>
                    </div>
                    
                    <label className="flex items-start gap-3 cursor-pointer p-3 bg-amber-100 rounded-lg border border-amber-300">
                      <Checkbox
                        checked={formData.cash_payment_waiver_agreed}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, cash_payment_waiver_agreed: checked }))}
                        className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-600"
                        data-testid="cash-waiver-checkbox"
                      />
                      <span className="text-sm text-amber-900">
                        <strong>I understand and agree</strong> that I must pay the full amount of <strong>${calculateTotal()}</strong> in cash to the worker before any service is provided. 
                        I acknowledge that if payment is not received, my booking will be cancelled and no service will be rendered.
                      </span>
                    </label>
                    
                    {!formData.cash_payment_waiver_agreed && (
                      <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        You must agree to the cash payment terms to continue
                      </p>
                    )}
                  </motion.div>
                )}
              </div>
              <div className="border border-[var(--border-primary)] rounded-xl p-6 mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">{waiver?.title}</h4>
                    <p className="text-sm text-[var(--text-secondary)]">Please read and sign the waiver to continue</p>
                  </div>
                </div>
                
                {formData.waiver_signed ? (
                  <div className="flex items-center gap-2 text-[var(--success)]">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Waiver signed</span>
                  </div>
                ) : (
                  <Button 
                    type="button" 
                    onClick={() => setShowWaiver(true)} 
                    className="btn-secondary"
                    data-testid="sign-waiver-btn"
                  >
                    Read & Sign Waiver
                  </Button>
                )}
              </div>

              {/* Non-Solicitation Agreement */}
              <div className="p-6 bg-[var(--warning)]/10 border-2 border-[var(--warning)]/30 rounded-xl">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800">Non-Solicitation Agreement</h4>
                    <p className="text-sm text-amber-700">Required to proceed with booking</p>
                  </div>
                </div>
                
                <div className="bg-[var(--background-paper)] p-4 rounded-lg mb-4 text-sm text-[var(--text-secondary)] max-h-32 overflow-y-auto">
                  <p className="mb-2">By checking this box, I agree to the following:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>I will NOT hire or attempt to hire</strong> any NC Help Hub worker directly outside of this platform.</li>
                    <li><strong>I will NOT share or request</strong> personal contact information from workers for private arrangements.</li>
                    <li><strong>I understand that all services</strong> must be booked and paid for through NC Help Hub.</li>
                    <li><strong>Violation of these terms</strong> may result in account termination and legal action.</li>
                  </ul>
                </div>
                
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.non_solicitation_agreed}
                    onChange={(e) => setFormData({...formData, non_solicitation_agreed: e.target.checked})}
                    className="w-5 h-5 mt-0.5 rounded border-amber-400 text-amber-600"
                    data-testid="non-solicitation-checkbox"
                  />
                  <span className="text-sm text-amber-800 font-medium">
                    I have read and agree to the Non-Solicitation Agreement. I understand I cannot hire NC Help Hub workers outside of this platform.
                  </span>
                </label>
              </div>

              {/* Service Scope & Payment Terms Agreement */}
              <div className="p-6 bg-[var(--accent)] border border-[var(--border-primary)] rounded-xl mt-6">
                <div className="flex items-start gap-3 mb-4">
                  <FileText className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Service Agreement</h4>
                    <p className="text-sm text-[var(--text-secondary)]">Please acknowledge the following terms</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Service Scope Acknowledgment */}
                  <label className="flex items-start gap-3 cursor-pointer p-3 bg-[var(--background-paper)] rounded-lg border border-[var(--border-primary)]">
                    <input
                      type="checkbox"
                      checked={formData.service_agreement.service_scope_acknowledged}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, 
                        service_agreement: {
                          ...prev.service_agreement,
                          service_scope_acknowledged: e.target.checked
                        }
                      }))}
                      className="w-5 h-5 mt-0.5 rounded border-[var(--border-primary)] accent-[var(--primary)]"
                      data-testid="service-scope-checkbox"
                    />
                    <span className="text-sm text-[var(--text-primary)]">
                      <strong>Service Scope Limitation:</strong> I understand that NC Help Hub provides <strong>non-medical services only</strong> (cleaning, errands, courier, laundry, tasks). Workers are NOT licensed healthcare providers and will NOT perform any medical, nursing, or CNA services.
                    </span>
                  </label>

                  {/* Payment Terms Agreement */}
                  <label className="flex items-start gap-3 cursor-pointer p-3 bg-[var(--background-paper)] rounded-lg border border-[var(--border-primary)]">
                    <input
                      type="checkbox"
                      checked={formData.service_agreement.payment_terms_agreed}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, 
                        service_agreement: {
                          ...prev.service_agreement,
                          payment_terms_agreed: e.target.checked
                        }
                      }))}
                      className="w-5 h-5 mt-0.5 rounded border-[var(--border-primary)] accent-[var(--primary)]"
                      data-testid="payment-terms-checkbox"
                    />
                    <span className="text-sm text-[var(--text-primary)]">
                      <strong>Payment Terms:</strong> I agree to pay the quoted amount for services rendered. I understand that cancellations within 24 hours of the scheduled service may incur a fee, and tips are optional but appreciated.
                    </span>
                  </label>
                </div>

                {(!formData.service_agreement.service_scope_acknowledged || !formData.service_agreement.payment_terms_agreed) && (
                  <p className="text-xs text-[var(--warning)] mt-3 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Both acknowledgments are required to confirm your booking
                  </p>
                )}
              </div>

              <div className="flex gap-4">
                <Button type="button" onClick={() => setStep(4)} className="btn-secondary flex-1">
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={!formData.waiver_signed || !formData.non_solicitation_agreed || !formData.non_medical_acknowledged || !formData.service_agreement.service_scope_acknowledged || !formData.service_agreement.payment_terms_agreed || submitting}
                  className="btn-primary flex-1"
                  data-testid="submit-booking"
                >
                  {submitting ? 'Creating Booking...' : 'Confirm Booking'}
                </Button>
              </div>
              
              {/* Helper text showing what's needed */}
              {(!formData.non_medical_acknowledged || !formData.waiver_signed || !formData.non_solicitation_agreed || !formData.service_agreement.service_scope_acknowledged || !formData.service_agreement.payment_terms_agreed) && (
                <p className="text-xs text-amber-600 mt-2 text-center">
                  Please complete all acknowledgments above to proceed
                </p>
              )}
            </motion.div>
          )}
        </form>
        )}
      </div>

      {/* Waiver Modal */}
      {showWaiver && (
        <div className="modal-overlay" onClick={() => setShowWaiver(false)}>
          <div className="modal-content max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">{waiver?.title}</h2>
            <div className="max-h-60 overflow-y-auto bg-[var(--background-subtle)] rounded-lg p-4 mb-6 text-sm whitespace-pre-line">
              {waiver?.content}
            </div>
            
            <div className="mb-4">
              <Label className="mb-2 block">Sign Below</Label>
              <div className="border border-[var(--border-primary)] rounded-xl bg-[var(--background-paper)]">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-40 rounded-xl',
                    style: { width: '100%', height: '160px' }
                  }}
                />
              </div>
              <button 
                type="button" 
                onClick={clearSignature}
                className="text-sm text-[var(--primary)] mt-2 hover:underline"
              >
                Clear Signature
              </button>
            </div>

            <div className="flex gap-4">
              <Button type="button" onClick={() => setShowWaiver(false)} className="btn-secondary flex-1">
                Cancel
              </Button>
              <Button type="button" onClick={handleSignature} className="btn-primary flex-1" data-testid="confirm-signature">
                Sign & Agree
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;
